/**
 * CondensedMessagingPopover — superfície aberta da mensageria condensada
 * (Mensageria Condensada · superfície 2).
 *
 * Casca + ROTEADOR de views internas: `list | thread | compose | peek`.
 * `list`/`thread` (F1a) + `compose` (CondensedComposer) e `peek` (LinkedContextPeek,
 * que decide internamente o estado `denied` a partir do 403) — F1b. A navegação
 * entre views NUNCA sai do popover (P13 — contexto vivo); a única saída deliberada
 * é "Ver tudo" → /mensagens.
 *
 * a11y: `role="dialog"` + `aria-modal="false"` (NÃO bloqueia o fundo — é glance
 * window). Esc fecha + retorna foco ao gatilho; clique-fora fecha. SEM scrim (de
 * propósito: um scrim apagaria o trabalho). `z-index: var(--z-overlay)`, ancorado
 * ao ícone com beak.
 */

import { ChevronRight, MessagesSquare, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useUnreadCount } from '../hooks/use-direct-messages';

import { CondensedComposer } from './components/CondensedComposer';
import { CondensedListView } from './components/CondensedListView';
import { CondensedThreadView } from './components/CondensedThreadView';
import { LinkedContextPeek } from './components/LinkedContextPeek';

// `peek` cobre permitido E negado: o LinkedContextPeek decide `denied` internamente
// a partir do 403 do context-peek (não há mais view `denied` separada).
type CondensedView = 'list' | 'thread' | 'compose' | 'peek';

interface CondensedMessagingPopoverProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
}

function unreadLabel(unread: number): string {
  if (unread <= 0) return 'Tudo em dia';
  const suffix = unread === 1 ? 'não lida' : 'não lidas';
  return `${unread.toString()} ${suffix}`;
}

export function CondensedMessagingPopover({
  anchorRef,
  onClose,
}: CondensedMessagingPopoverProps): JSX.Element {
  const [view, setView] = useState<CondensedView>('list');
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [peekConversationId, setPeekConversationId] = useState<string | null>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const { unread } = useUnreadCount();

  // Esc fecha + retorna foco ao gatilho; clique-fora fecha. SEM scrim.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
        anchorRef.current?.focus();
      }
    };
    const onDown = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (
        popRef.current !== null &&
        !popRef.current.contains(target) &&
        anchorRef.current?.contains(target) !== true
      ) {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose, anchorRef]);

  const openThread = useCallback((id: string) => {
    setActiveThreadId(id);
    setView('thread');
  }, []);

  // O chip âncora abre a espiada (peek) DENTRO do popover. Guarda o id da conversa
  // ancorada; o LinkedContextPeek busca o contexto e decide permitido × negado (403).
  const openPeek = useCallback((conversationId: string) => {
    setPeekConversationId(conversationId);
    setView('peek');
  }, []);

  return (
    <div
      ref={popRef}
      className="mc-popover"
      role="dialog"
      aria-modal="false"
      aria-label="Mensagens internas"
    >
      <span className="mc-beak" aria-hidden />

      <header className="mc-pop-header">
        <span className="mc-pop-glyph" aria-hidden>
          <MessagesSquare size={14} strokeWidth={1.6} />
        </span>
        <div className="mc-pop-title">
          <b>Mensagens internas</b>
          <span className="mc-mono-meta">{unreadLabel(unread)}</span>
        </div>
        {/* "Ver tudo" → /mensagens (única saída; navega de propósito). */}
        <Link className="mc-ghost-link" to="/mensagens" onClick={onClose}>
          Ver tudo
          <ChevronRight size={12} strokeWidth={1.6} aria-hidden />
        </Link>
        <button type="button" className="mc-icon-btn" aria-label="Fechar" onClick={onClose}>
          <X size={14} strokeWidth={1.6} aria-hidden />
        </button>
      </header>

      {view === 'list' && (
        <CondensedListView
          onOpenThread={openThread}
          onCompose={() => {
            setView('compose');
          }}
          onPeek={openPeek}
        />
      )}

      {view === 'thread' && activeThreadId !== null && (
        <CondensedThreadView
          threadId={activeThreadId}
          onBack={() => {
            setView('list');
          }}
          onPeek={openPeek}
        />
      )}

      {/* ── Views F1b — compose / peek (peek decide denido do 403 internamente) ── */}
      {view === 'compose' && (
        <CondensedComposer
          onBack={() => {
            setView('list');
          }}
          onSent={openThread}
        />
      )}
      {view === 'peek' && (
        <LinkedContextPeek
          conversationId={peekConversationId}
          viaThreadId={activeThreadId}
          onBack={() => {
            setView(activeThreadId !== null ? 'thread' : 'list');
          }}
        />
      )}
    </div>
  );
}
